import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { buildBreadcrumbLd } from '../lib/structuredData';

const SITE_URL = 'https://residentialsupport.co.uk';

const H2 = ({ children, id }) => (
  <h2 id={id} className="h-display text-2xl md:text-[1.7rem] text-[var(--rs-ink)] mt-12 mb-3 leading-snug scroll-mt-24">
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="h-display text-lg md:text-xl text-[var(--rs-ink)] mt-8 mb-2 leading-snug">
    {children}
  </h3>
);

const P = ({ children }) => (
  <p className="text-[var(--rs-ink)]/80 leading-relaxed mt-3">{children}</p>
);

const UL = ({ children }) => (
  <ul className="my-4 space-y-2 list-disc pl-6 text-[var(--rs-ink)]/80">{children}</ul>
);

const Table = ({ headers, rows }) => (
  <div className="my-6 overflow-x-auto rounded-lg border border-[#E7E0D2]">
    <table className="w-full text-sm text-left">
      <thead className="bg-[var(--rs-sand)] text-[var(--rs-ink)]">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className="px-4 py-3 font-semibold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className="border-t border-[#EDE7D6] text-[var(--rs-ink)]/80">
            {row.map((cell, ci) => (
              <td key={ci} className="px-4 py-3 align-top">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Privacy = () => {
  return (
    <div>
      <PageMeta
        title="Privacy Policy · Residential Support"
        description="How Residential Support Ltd collects, uses, stores and protects your personal data. UK GDPR rights, cookies, third-party processors and contact details."
        path="/privacy"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(
            buildBreadcrumbLd([
              { label: 'Home', path: '/' },
              { label: 'Privacy', path: '/privacy' },
            ])
          )}
        </script>
      </Helmet>

      {/* Header */}
      <section className="bg-[var(--rs-cream)] border-b border-[#E7E0D2]">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-10">
          <nav aria-label="Breadcrumb" className="text-xs text-[var(--rs-ink)]/55 mb-5">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:text-[var(--rs-coral)]">Home</Link></li>
              <li><ChevronRight className="w-3 h-3 text-[var(--rs-ink)]/40" /></li>
              <li className="text-[var(--rs-ink)]/80 font-medium">Privacy</li>
            </ol>
          </nav>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">Legal</div>
          <h1 className="h-display text-4xl md:text-5xl lg:text-6xl text-[var(--rs-ink)] leading-[1.05]" data-testid="privacy-title">
            Privacy Policy
          </h1>
          <p className="mt-4 text-[var(--rs-ink)]/65 text-sm">Last updated: November 2026</p>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12 lg:py-16" data-testid="privacy-body">
          <H2 id="introduction">1. Introduction</H2>
          <P>
            This privacy notice explains how we collect and process your personal data through your use of our website and services, whether as a business customer (B2B) or an individual consumer (B2C). This includes information you provide when registering, purchasing services, contacting us, subscribing to communications, or using our website.
          </P>
          <P>By providing your data, you confirm that you are over 16 years of age.</P>
          <P>
            Residential Support is a trading name of Agents Support Ltd, which is the data controller responsible for your personal data (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;).
          </P>

          <H3>Contact details</H3>
          <UL>
            <li>Legal entity: Agents Support Ltd (trading as Residential Support)</li>
            <li>Company number: 1624825</li>
            <li>Address: 20 Allandale Road, Leicester, England, LE2 2DA</li>
            <li>Email: <a className="text-[var(--rs-coral)] hover:underline" href="mailto:info@residentialsupport.co.uk">info@residentialsupport.co.uk</a></li>
            <li>Telephone: 03333 445 336</li>
          </UL>
          <P>
            You have the right to complain to the Information Commissioner&apos;s Office (ICO). We would appreciate the opportunity to resolve any concerns first.
          </P>
          <P>Please inform us if your personal information changes.</P>

          <H2 id="what-we-collect">2. What data we collect</H2>
          <P>We may collect:</P>
          <UL>
            <li>Identity Data — name, title, date of birth, gender</li>
            <li>Contact Data — address, email, phone number</li>
            <li>Financial Data — bank details, card details (processed securely via Stripe)</li>
            <li>Transaction Data — payments and services purchased</li>
            <li>Technical Data — IP address, browser type, device info, OS</li>
            <li>Profile Data — account details, preferences, feedback</li>
            <li>Usage Data — how you use our website</li>
            <li>Marketing Data — communication preferences</li>
          </UL>
          <P>We may also use aggregated anonymous data.</P>

          <H2 id="sensitive-data">3. Sensitive data</H2>
          <P>We do not collect special category or criminal conviction data.</P>

          <H2 id="how-we-collect">4. How we collect your data</H2>
          <UL>
            <li>Direct interactions (forms, email, phone, contracts)</li>
            <li>Automated technologies (cookies, analytics)</li>
            <li>
              Third parties including:
              <ul className="mt-2 space-y-1 list-[circle] pl-6">
                <li>Google Analytics</li>
                <li>Stripe</li>
                <li>Hosting and IT providers</li>
                <li>Companies House and public registers</li>
              </ul>
            </li>
          </UL>

          <H2 id="how-we-use">5. How we use your data</H2>
          <P>We use personal data when necessary for:</P>
          <UL>
            <li>Performing contracts</li>
            <li>Legal obligations</li>
            <li>Legitimate business interests</li>
            <li>Consent (mainly marketing)</li>
          </UL>
          <H3>Lawful basis summary (UK GDPR)</H3>
          <Table
            headers={['Purpose', 'Data types', 'Lawful basis']}
            rows={[
              ['Register users', 'Identity, Contact', 'Contract'],
              ['Provide services', 'Identity, Contact, Transaction', 'Contract'],
              ['Process payments', 'Financial, Transaction', 'Contract'],
              ['Customer support', 'Identity, Contact', 'Legitimate interest'],
              ['Marketing', 'Contact', 'Consent'],
              ['Website analytics', 'Technical, Usage', 'Legitimate interest'],
              ['Fraud prevention', 'Identity, Technical', 'Legitimate interest'],
              ['Legal compliance', 'Identity, Transaction', 'Legal obligation'],
            ]}
          />

          <H2 id="marketing">6. Marketing communications</H2>
          <P>
            You may receive marketing if you have used our services or requested information and have not opted out.
          </P>
          <P>
            You can opt out anytime by emailing{' '}
            <a className="text-[var(--rs-coral)] hover:underline" href="mailto:info@residentialsupport.co.uk">
              info@residentialsupport.co.uk
            </a>
            .
          </P>
          <P>We never sell your data.</P>

          <H2 id="data-sharing">7. Data sharing</H2>
          <P>We may share data with:</P>
          <UL>
            <li>IT &amp; hosting providers</li>
            <li>Stripe (payments)</li>
            <li>Google (analytics)</li>
            <li>Professional advisers</li>
            <li>HMRC and regulators</li>
            <li>Business partners required to deliver services</li>
            <li>Buyers in a business sale or restructure</li>
          </UL>
          <P>All third parties must comply with UK GDPR.</P>

          <H2 id="international">8. International transfers</H2>
          <P>Some providers (e.g. Stripe, Google) process data outside the UK.</P>
          <P>We safeguard transfers using:</P>
          <UL>
            <li>UK adequacy regulations</li>
            <li>International Data Transfer Agreements (IDTA)</li>
            <li>Standard Contractual Clauses</li>
          </UL>

          <H2 id="security">9. Data security</H2>
          <P>
            We use appropriate technical and organisational security measures and restrict access to authorised personnel only.
          </P>

          <H2 id="retention">10. Data retention</H2>
          <P>We retain data only as long as necessary.</P>
          <P>Tax and financial records are kept for 6 years.</P>
          <P>You may request deletion where legally possible.</P>

          <H2 id="rights">11. Your legal rights</H2>
          <P>You may:</P>
          <UL>
            <li>Access your data</li>
            <li>Correct your data</li>
            <li>Request erasure</li>
            <li>Object to processing</li>
            <li>Restrict processing</li>
            <li>Request data portability</li>
            <li>Withdraw consent</li>
          </UL>
          <P>
            Email:{' '}
            <a className="text-[var(--rs-coral)] hover:underline" href="mailto:info@residentialsupport.co.uk">
              info@residentialsupport.co.uk
            </a>
            .
          </P>

          <H2 id="third-party-links">12. Third-party links</H2>
          <P>We are not responsible for third-party websites.</P>

          <H2 id="cookies">13. Cookies</H2>
          <P>See the Cookie Policy below.</P>

          <H2 id="location-data">14. Location Data</H2>

          <H3>Collection of Location Information</H3>
          <P>
            The Residential Support application collects location information when you use the application and, where permission has been granted, may also collect location information while the application is running in the background.
          </P>
          <P>The application may collect:</P>
          <UL>
            <li>Precise GPS location.</li>
            <li>Approximate location.</li>
            <li>Background location (&ldquo;Allow all the time&rdquo;), where enabled by the user.</li>
          </UL>

          <H3>Why We Collect Location Data</H3>
          <P>Location information is collected to support the services provided through the application, including:</P>
          <UL>
            <li>Verifying that property inspections are carried out at the correct address.</li>
            <li>Recording the location where inspection reports and photographs are created.</li>
            <li>Providing accurate timestamps and location verification for completed inspections.</li>
            <li>Supporting audit trails and fraud prevention.</li>
            <li>Improving the reliability and quality of our inspection services.</li>
          </UL>
          <P>
            Background location may be collected where necessary to support active inspection workflows or other application features that continue while the application is running in the background.
          </P>

          <H3>How We Use Location Data</H3>
          <P>
            We use location information only for legitimate business purposes related to the operation of our property inspection services.
          </P>
          <P>We do not use location information for advertising or marketing purposes.</P>

          <H3>Sharing of Location Data</H3>
          <P>Location information may be shared only with:</P>
          <UL>
            <li>Residential Support employees.</li>
            <li>Clients who have instructed Residential Support to carry out inspections on their behalf.</li>
            <li>Trusted cloud service providers who securely process data on our behalf.</li>
          </UL>
          <P>
            We do not sell, rent or otherwise disclose your location information to third parties for marketing purposes.
          </P>

          <H3>Your Choices</H3>
          <P>You may grant, deny or withdraw location permissions at any time through your device settings.</P>
          <P>
            Please note that disabling location permissions, including background location, may prevent certain features of the application from functioning correctly or may prevent inspections from being completed.
          </P>

          <H3>Data Retention</H3>
          <P>
            Location information is retained only for as long as necessary to provide our services, comply with legal obligations, resolve disputes and enforce our agreements.
          </P>

          <H3>Security</H3>
          <P>
            Residential Support implements appropriate technical and organisational security measures to protect location information against unauthorised access, disclosure, alteration or destruction.
          </P>

          <H3>Data processing activities register</H3>
          <Table
            headers={['Activity', 'Data subjects', 'Data types', 'Processor', 'Location', 'Retention']}
            rows={[
              ['Account registration', 'Businesses & individuals', 'Name, email, phone', 'Internal', 'UK', '6 years'],
              ['Payment processing', 'Customers', 'Card & billing data', 'Stripe', 'UK/USA', 'As per Stripe'],
              ['Website analytics', 'Website users', 'IP, device data', 'Google', 'USA', '26 months'],
              ['Customer support', 'Customers', 'Contact history', 'Internal', 'UK', '3 years'],
              ['Marketing emails', 'Subscribers', 'Email', 'Email provider', 'UK/EEA', 'Until opt-out'],
              ['Accounting', 'Customers', 'Transaction data', 'Accountant', 'UK', '6 years'],
            ]}
          />

          {/* ── Cookie Policy ─────────────────────────────────────── */}
          <div className="mt-16 pt-12 border-t border-[#E7E0D2]">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">Cookies</div>
            <h2 id="cookie-policy" className="h-display text-3xl md:text-4xl text-[var(--rs-ink)] leading-snug">
              Cookie Policy
            </h2>
            <p className="mt-3 text-sm text-[var(--rs-ink)]/60">Version: January 2026</p>

            <P>
              Residential Support (a trading name of Agents Support Ltd) uses cookies to improve website functionality, analyse traffic, and support marketing.
            </P>

            <H3>What are cookies?</H3>
            <P>Cookies are small text files stored on your device.</P>

            <H3>Cookie table</H3>
            <Table
              headers={['Cookie name', 'Provider', 'Purpose', 'Type', 'Duration']}
              rows={[
                ['_ga', 'Google Analytics', 'Visitor tracking', 'Analytics', '2 years'],
                ['ga*', 'Google Analytics', 'Session tracking', 'Analytics', '2 years'],
                ['_gid', 'Google Analytics', 'User identification', 'Analytics', '24 hours'],
                ['_stripe_mid', 'Stripe', 'Fraud prevention', 'Necessary', '1 year'],
                ['_stripe_sid', 'Stripe', 'Payment processing', 'Necessary', 'Session'],
                ['cookie_consent', 'Residential Support', 'Stores consent choice', 'Necessary', '6 months'],
              ]}
            />

            <H3>Cookie categories</H3>
            <UL>
              <li>Strictly necessary</li>
              <li>Analytics</li>
              <li>Functionality</li>
              <li>Marketing</li>
            </UL>

            <H3>Consent</H3>
            <P>On your first visit you can:</P>
            <UL>
              <li>Accept all cookies</li>
              <li>Reject non-essential cookies</li>
              <li>Manage preferences</li>
            </UL>
            <P>You can also control cookies via your browser.</P>

            <H3>International transfers</H3>
            <P>
              Google and Stripe may process data outside the UK using approved safeguards.
            </P>

            <H3>Contact</H3>
            <P>
              <a className="text-[var(--rs-coral)] hover:underline" href="mailto:info@residentialsupport.co.uk">
                info@residentialsupport.co.uk
              </a>{' '}
              · 03333 445 336
            </P>
          </div>

          <div className="mt-14 pt-8 border-t border-[#E7E0D2] text-sm text-[var(--rs-ink)]/65">
            <p>
              Looking for our website terms?{' '}
              <Link to="/terms" className="text-[var(--rs-coral)] hover:underline">Read the Terms of Use</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
